import { Component, OnInit } from '@angular/core';
import { CompanyManagementService } from './shared/services/companyManagement.service';
import { Company } from './shared/models/Company';
import { Country } from './shared/models/Country';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Params } from '@angular/router';
import { Subscription, catchError, throwError } from 'rxjs';
import { SpinnerService } from 'src/core/services/spinner.service';
import { MessageService } from 'primeng/api';
import { Label } from 'src/core/Models/label.model';
import { LabelService } from 'src/core/services/label.service';
import { NotificationService } from 'src/app/search-assign/shared/services/notification.service';
import { UserRole } from 'src/core/helpers/user-roles';
import { parseAddress } from './shared/helpers/address-parser';


@Component({
  selector: 'app-company-settings',
  templateUrl: './company-settings.component.html',
  styleUrls: ['./company-settings.component.scss'],

})

export class CompanySettingsComponent implements OnInit {
  showVatModal: boolean = false;
  paramsSubscription!: Subscription;
  companyInfo: Company = {};
  organizationId: number | null = localStorage.getItem('organizationId') ? Number(localStorage.getItem('organizationId')) : null;
  currentUser: any = JSON.parse(localStorage.getItem('currentUser')!);
  currentUserRole = {
    id: this.currentUser.board.roles[0],
  };
  countryCode: string = '';
  countryEU: boolean = true;
  DisconnectVatID: boolean = false;
  routerSubscription!: Subscription;
  newCompany: boolean = true;
  ValidVatID: boolean = false;
  animationState: string = '';
  countries: Country[] = [];
  companyForm: FormGroup;
  Denmark: boolean = false;
  Norway: boolean = false;
  isAdmin: boolean = false;
  hasMembership: boolean = false;
  isCompanyPersonal: boolean = false;
  boardId!: string;
  editLabelSubscription!: Subscription;
  nameOfCollectingLabel: string = ''; 

  constructor(private companyService: CompanyManagementService,
    private fb: FormBuilder, private route: ActivatedRoute,
    private notificationService: NotificationService,
    private spinnerService: SpinnerService,
    private messageService: MessageService,
    private labelService: LabelService) {
    this.companyForm = this.fb.group({
      companyType: ['', Validators.required],
      registeredCountry: ['', Validators.required],
      vatid: ['', Validators.required],
      brandName: ['', Validators.required],
      legalName: [{ value: '', disabled: true }],
      city: [{ value: '', disabled: true }],
      zip: [{ value: '', disabled: true }],
      address: [{ value: '', disabled: true }],
      accountNumber: [''],
      registrationNumber: [''],
      bankName: [''],
      swiftBic: [''],
      boardId: ['']
    });
  }

  private enableAddressFields(): void {
    this.companyForm.get('brandName')?.enable();
    this.updateAddressFieldsState();
  }

  private updateAddressFieldsState(): void {
    if (this.ValidVatID) {
      // When VAT is valid, disable address fields for everyone
      this.companyForm.get('city')?.disable();
      this.companyForm.get('zip')?.disable();
      this.companyForm.get('address')?.disable();
    } else {
      // When VAT is invalid/empty, enable for admins only
      if (this.isAdmin) {
        this.companyForm.get('city')?.enable();
        this.companyForm.get('zip')?.enable();
        this.companyForm.get('address')?.enable();
      } else {
        this.companyForm.get('city')?.disable();
        this.companyForm.get('zip')?.disable();
        this.companyForm.get('address')?.disable();
      }
    }
  }

  ngOnInit(): void {
    this.getCountries();

    this.isAdmin = UserRole.Superadmin == this.currentUserRole.id;

    this.syncRegisteredCountry();

    this.paramsSubscription = this.route.params.subscribe((params: Params) => {
      this.boardId = params['id'] || null;
      this.loadData();
    });
  }

  private patchFormWithCompanyDetails(): void {

    let accountNumber = '';
    if (this.companyInfo.registeredCountry === 'Denmark') {
      accountNumber = this.companyInfo.dK_AccountNo || '';
    } else if (this.companyInfo.registeredCountry === 'Norway') {
      accountNumber = this.companyInfo.nO_AccountNo || '';
    } else {
      accountNumber = this.companyInfo.intl_IBAN || '';
    }

    this.companyForm.patchValue({
      companyType: this.companyInfo.companyType,
      registeredCountry: this.companyInfo.registeredCountry,
      vatid: this.companyInfo.vatid,
      brandName: this.companyInfo.brandName,
      legalName: this.companyInfo.legalName,
      city: this.companyInfo.city,
      zip: this.companyInfo.zip,
      address: this.companyInfo.address,
      accountNumber: accountNumber,
      registrationNumber: this.companyInfo.dK_RegNo,
      bankName: this.companyInfo.intl_NameOfBank,
      swiftBic: this.companyInfo.intl_Swicfbic,
      boardId: this.companyInfo.boardId
    });

  }

  getCountries() {
    this.companyService.getCountries().subscribe(
      data => {
        this.countries = data;
      }
    );
  }

  onSubmit() {
    if (this.companyForm.valid) {
      const formValue = this.companyForm.value;
    }
  }

  validateVat() {
    this.showVatModal = true;

    //this.spinnerService.show();
    this.countryCode = this.countries.find(x => x.name == this.companyForm.value.registeredCountry)?.code || '';
    this.countryEU = this.countries.find(x => x.name == this.companyForm.value.registeredCountry)?.isEU || false;

    this.companyService.ValidateVatID(this.countryCode, this.countryEU, this.companyForm.value.vatid, this.companyForm.value.boardId).subscribe({
      next: (response) => {
        this.ValidVatID = response.isValid === true;

        if (response.isValid) {
          this.companyForm.patchValue(response);

          // Specifically assign address and legalName from the VAT response
          if (response.address) {
            // VAT validation returns address, zip and city as one raw string.
            // Parse it into separate fields using country-specific rules.
            const parsed = parseAddress(response.address, this.countryCode);

            this.companyForm.get('address')?.setValue(parsed.address || response.address);
            if (parsed.zip) {
              this.companyForm.get('zip')?.setValue(parsed.zip);
            }
            if (parsed.city) {
              this.companyForm.get('city')?.setValue(parsed.city);
            }

            // Only disable address fields for non-admin users
            if (!this.isAdmin) {
              this.companyForm.get('address')?.disable();
            }
          }

          // Assign the company legal name from VAT response
          if (response.legalName) {
            this.companyForm.get('legalName')?.setValue(response.legalName);
            this.companyForm.get('legalName')?.disable();
          }

          // Disable the rest of the form
          this.companyForm.disable();
          // Re-enable brandName for admin users
          if (this.isAdmin) {
            this.companyForm.get('brandName')?.enable();
          }
          // Address fields remain disabled when VAT is valid
          this.updateAddressFieldsState();
          this.toggleDisconnectVatID();
          this.ValidVatID = true;
          this.messageService.add({
            severity: 'success',
            summary: 'Validated VatID',
            detail: 'Valid VatID',
          });

          this.UpdateCompany();
        } else {
          this.ValidVatID = false;
          this.companyForm.enable();
          // Always keep legalName disabled
          this.companyForm.get('legalName')?.disable();
          // Update address fields based on admin status
          this.updateAddressFieldsState();
          this.messageService.add({
            severity: 'error',
            summary: 'Invalid VAT ID',
            detail: 'The VAT ID you entered is not valid. Please correct it before saving.',
          });
        }
        //this.spinnerService.hide();
        this.showVatModal = false;
      },
      error: (error) => {
        //this.spinnerService.hide();
        this.ValidVatID = false;
        this.messageService.add({
          severity: 'error',
          summary: 'VAT ID Validation Failed',
          detail: 'Please check the VAT ID and try again',
        });
        this.showVatModal = false;
      }
    });
  }

  private syncRegisteredCountry(): void {
    this.companyForm.get('registeredCountry')?.valueChanges.subscribe((value) => {
      this.updatePageBasedOnCountry(value);
      this.companyForm.patchValue({ registeredCountry: value }, { emitEvent: false });
    });
  }

  UpdateCompany() {
    const formValues = this.companyForm.getRawValue();

    const company: Company = {
      ...this.companyInfo,
      countryId: this.countries.find(x => x.name == formValues.registeredCountry)?.id || undefined,
      legalName: formValues.legalName,
      brandName: formValues.brandName,
      vatid: formValues.vatid,
      city: formValues.city,
      zip: formValues.zip,
      address: formValues.address,
      registeredCountry: formValues.registeredCountry,
      companyType: formValues.companyType,
      isFullyCreated: true,
      boardId: this.boardId,
      isVatIdValid: this.ValidVatID
    };

    this.companyService.UpdateCompany(company).subscribe({
      next: (response) => {
        if (this.companyForm.value.brandName !== this.companyInfo.brandName) {
          this.editLabel();
        }
        this.messageService.add({
          severity: 'success',
          summary: 'Settings Saved',
          detail: 'Your changes have been Successfully Updated.',
        });
      },
      error: (error) => {
        this.messageService.add({
          severity: 'error',
          summary: 'Settings Saved Error',
          detail: 'Please Try Again.',
        });
      }
    });

    this.animationState = 'slide-down';
    setTimeout(() => {
      this.companyForm.markAsPristine();
      this.animationState = '';
    }, 200);
  }

  toggleDisconnectVatID() {
    this.DisconnectVatID = !this.DisconnectVatID;
    if (this.DisconnectVatID) {
      this.companyForm.disable();
      // Re-enable brandName for admin users
      if (this.isAdmin) {
        this.companyForm.get('brandName')?.enable();
      }
      // Address fields remain disabled when VAT is valid
      this.updateAddressFieldsState();
    } else {
      this.companyForm.enable();
      // Always keep legalName disabled
      this.companyForm.get('legalName')?.disable();
      // Update address fields based on admin status
      this.updateAddressFieldsState();
    }
  }

  public cancel(): void {
    this.animationState = 'slide-down';
    setTimeout(() => {
      this.companyForm.markAsPristine();
      this.animationState = '';
    }, 200);
    this.patchFormWithCompanyDetails();
  }

  changeCountryType(event: any) {
    const selectedType = event.target.value;
    if (selectedType === 'Business') {
      this.isCompanyPersonal = false;
    } else if (selectedType === 'Personal') {
      this.isCompanyPersonal = true;
    }
  }

  updatePageBasedOnCountry(country: string) {
    if (country === 'Denmark') {
      this.Denmark = true;
      this.Norway = false;
    } else if (country === 'Norway') {
      this.Denmark = false;
      this.Norway = true;
    } else {
      this.Denmark = false;
      this.Norway = false;
    }
  }


  private loadCollectingLabelName(): void {
    if (this.companyInfo.collectorId) {
      this.labelService.getLabel(this.companyInfo.collectorId).subscribe({
        next: (labelResponse) => {
          this.nameOfCollectingLabel = labelResponse.Name || '';
        },
        error: (labelError) => {
          console.error('Error fetching collecting label:', labelError);
          this.nameOfCollectingLabel = '';
        }
      });
    } else {
      this.nameOfCollectingLabel = '';
    }
  }

  loadData(): void {
    if (!this.boardId) return;
    //this.spinnerService.show();
    this.companyService.getCompanybyBoardID(this.boardId).subscribe({
      next: (response) => {
        this.companyInfo = response;
        this.hasMembership = (this.companyInfo.ifpiDanmark || this.companyInfo.ifpiNorge || this.companyInfo.fono) ? true : false;  

        this.isCompanyPersonal = this.companyInfo.companyType == 'Personal' ? true : false;

        if (this.companyInfo) {
          this.newCompany = false;
          this.patchFormWithCompanyDetails();


          if (this.companyInfo.vatid) {
            // Use the isVatIdValid flag from the API
            this.ValidVatID = this.companyInfo.isVatIdValid === true;

            this.DisconnectVatID = this.ValidVatID;

            // Update form state based on VAT validation
            if (this.ValidVatID) {
              this.companyForm.disable();
              // Re-enable brandName for admin users
              if (this.isAdmin) {
                this.companyForm.get('brandName')?.enable();
              }
              // Address fields remain disabled when VAT is valid
              this.updateAddressFieldsState();
            } else {
              this.companyForm.enable();
              // Always keep legalName disabled
              this.companyForm.get('legalName')?.disable();
              // Update address fields based on admin status
              this.updateAddressFieldsState();
            }
          } else {
            // No VAT ID to validate
            this.ValidVatID = false;
            this.DisconnectVatID = false;
          }

          this.Denmark = this.companyInfo.registeredCountry == 'Denmark' ? true : false;
          this.Norway = this.companyInfo.registeredCountry == 'Norway' ? true : false;
          this.hasMembership = (this.companyInfo.ifpiDanmark || this.companyInfo.ifpiNorge || this.companyInfo.fono) ? true : false;
        } else {
          const companyTypeControl = this.companyForm.get('companyType');

          if (companyTypeControl)
            companyTypeControl.setValue('Business');

          //this.spinnerService.hide();
          this.newCompany = true;
        }
        
        //this.spinnerService.hide();
      },
      error: (error) => {
        //this.spinnerService.hide();
        this.notificationService.showError('Company Not Exist please Fill Company Data');
      }
    });
  }

  closeVatModal() {
    this.showVatModal = false;
  }

  disconnectVatId(): void {
    // Reset VAT-related fields
    this.companyForm.get('vatid')?.setValue('');
    this.companyForm.get('legalName')?.setValue('');

    // Enable the form for editing again
    this.companyForm.enable();

    // Keep legal name disabled as it should always be
    this.companyForm.get('legalName')?.disable();

    // Update address fields based on admin status (VAT is now invalid)
    this.updateAddressFieldsState();

    // Update validation status
    this.ValidVatID = false;
    this.DisconnectVatID = false;

    // Notify user
    this.messageService.add({
      severity: 'info',
      summary: 'VAT ID Disconnected',
      detail: 'VAT ID has been removed. You can now enter a new VAT ID or save without one.'
    });
  }

  editLabel() {
    if (!this.companyInfo.boardId) {
      console.error('Cannot edit label: boardId is undefined');
      return;
    }

    let labelData: Label = {
      id: this.companyInfo.boardId!,
      name: this.companyForm.value.brandName,
      title: this.companyForm.value.brandName,
      hasImage: false,
      isCollectingForItself: this.companyInfo.isCollectingForItself ?? false,
      parentBoardId: this.companyInfo.parentBoardId ?? this.currentUser.board.id
    };

    this.editLabelSubscription = this.labelService.editLabel(labelData).pipe(
      catchError((error) => {
        return throwError(() => {
          console.error('Error editing label:', error);
        });
      })
    ).subscribe();
  }
}